import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { bigQueryLocations } from '@/data/inventory/regions';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreBigQueryState } from '../restoreBigQueryWizard';

const locationOptions = Object.entries(bigQueryLocations).map(
  ([value, { displayName }]) => ({
    value,
    label: `${value} - ${displayName}`,
  })
);

const datasetIdConfig = getCloudFieldConfig(Provider.Gcp, 'bigQueryDatasetId', {
  required: true,
});

export const BigQueryConfiguration = (
  props: StepProps<RestoreBigQueryState>
) => {
  const {
    wizardState: stepState,
    setWizardState,
    stepperLabels,
    back,
    finish,
  } = props;
  const {
    restoreAccount,
    snapshot,
    resourceId,
    targetDatasetId,
    targetLocation,
    availableTables,
    selectedTables,
  } = stepState;

  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const onNextClick = async () => {
    setError(undefined);
    setIsRestoring(true);

    void dal.restore.bigQuery
      .restore(resourceId, snapshot.id, {
        restoreAccountId: restoreAccount!.id,
        destination: {
          datasetId: targetDatasetId,
          location: targetLocation,
          selectedTables:
            selectedTables.length === availableTables.length
              ? undefined
              : selectedTables,
        },
      })
      .then(() =>
        finish(`Started restore job for BigQuery dataset ${targetDatasetId}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore BigQuery dataset: ${errorMessage}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  const canGoNext = () => {
    if (isRestoring) {
      return false;
    }
    const isDatasetIdValid =
      datasetIdConfig.validation.validateInput(targetDatasetId);
    return isDatasetIdValid && selectedTables.length > 0;
  };

  const tableOptions = availableTables.map((t) => t.name || '');

  const selectedLocationOption =
    locationOptions.find((opt) => opt.value === targetLocation) || null;

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={canGoNext}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={snapshot.pointInTime}
          vaultId={snapshot.vaultId}
          accessDeniedItemsCount={snapshot.accessDeniedItemsCount}
          supportPiTR={snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='column' gap='24px'>
          <Stack direction='row' gap='16px'>
            <FormTextField
              title='Target Dataset ID'
              validation={datasetIdConfig.validation}
              placeholder={datasetIdConfig.placeholder}
              value={targetDatasetId}
              onChange={(e) =>
                setWizardState((state) => ({
                  ...state,
                  targetDatasetId: e.target.value,
                }))
              }
              sx={{ width: '50%' }}
            />

            <FormControl size='small' sx={{ width: '50%' }}>
              <FormLabel>Location</FormLabel>
              <Autocomplete
                data-testid='targetLocationSelect'
                size='small'
                options={locationOptions}
                value={selectedLocationOption}
                onChange={(_, newValue) =>
                  setWizardState((state) => ({
                    ...state,
                    targetLocation: newValue?.value || '',
                  }))
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder='Select location' />
                )}
              />
            </FormControl>
          </Stack>

          <FormControl size='small' className='flex-1'>
            <FormLabel>Tables to Restore</FormLabel>
            <Autocomplete
              data-testid='tablesSelect'
              multiple
              disableCloseOnSelect
              limitTags={4}
              size='small'
              options={tableOptions}
              value={tableOptions.filter((opt) => selectedTables.includes(opt))}
              onChange={(_, newValue) =>
                setWizardState((state) => ({
                  ...state,
                  selectedTables: newValue,
                }))
              }
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder='Select tables' />
              )}
            />
          </FormControl>
        </Stack>
      </Box>
    </StepContainer>
  );
};
